import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useState } from "react";
import { DuckBadge } from "@/components/DuckMascot";
import { LEGAL_FOOTER } from "@/data/site";
import { subscribeNewsletter } from "@/lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const res = await subscribeNewsletter(email, "footer");
      toast.success(res.status === "already_subscribed" ? "You're already on the list!" : "You're in. Welcome aboard.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const col = (title, links) => (
    <div>
      <h4 className="font-mono text-[11px] uppercase tracking-[0.2em] text-cream/50 mb-4">{title}</h4>
      <ul className="space-y-2.5">
        {links.map((l) => (
          <li key={l.to}>
            <Link to={l.to} className="text-sm text-cream/75 hover:text-duckgreen transition-colors">
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <footer className="bg-forest-950 text-cream" data-testid="site-footer">
      {/* Newsletter band */}
      <div className="border-b border-cream/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-14 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="font-serif text-2xl sm:text-3xl font-bold text-cream">
              No scare tactics. Just useful pest advice.
            </h3>
            <p className="mt-2 text-cream/70 text-sm max-w-md">
              Join the list for practical, seasonal, homeowner-first guidance. No spam, unsubscribe anytime.
            </p>
          </div>
          <form onSubmit={onSubscribe} className="flex gap-3" data-testid="footer-newsletter-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="flex-1 min-w-0 px-5 py-3.5 rounded-full bg-cream/10 border border-cream/20 text-cream placeholder:text-cream/40 focus:border-duckgreen outline-none transition-colors"
              data-testid="footer-newsletter-input"
            />
            <button
              type="submit"
              disabled={busy}
              className="px-6 py-3.5 rounded-full bg-duckgreen text-forest-950 font-semibold hover:bg-duckgreen/90 transition-colors disabled:opacity-60 whitespace-nowrap"
              data-testid="footer-newsletter-submit"
            >
              {busy ? "…" : "Subscribe"}
            </button>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-14">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-10">
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-2.5 mb-4">
              <DuckBadge size={44} ring={false} />
              <span className="font-serif text-lg font-bold text-cream">DuckTheBugs</span>
            </Link>
            <p className="text-sm text-cream/60 leading-relaxed">
              The homeowner's guide to bugs, bites, and backyard pests.
            </p>
          </div>
          {col("Pests", [
            { to: "/pests/mosquitoes", label: "Mosquitoes" },
            { to: "/pests/ticks", label: "Ticks" },
            { to: "/pests/wasps-hornets", label: "Wasps & Hornets" },
            { to: "/pests/ants", label: "Ants" },
            { to: "/pests/fleas", label: "Fleas" },
            { to: "/pests/rodents", label: "Rodents" },
          ])}
          {col("Resources", [
            { to: "/diy", label: "DIY Hub" },
            { to: "/guides", label: "Guides" },
            { to: "/hire-a-pro", label: "Find a Pro" },
            { to: "/connecticut/madison/mosquito-control", label: "Local: Madison, CT" },
          ])}
          {col("Company", [
            { to: "/about", label: "About" },
            { to: "/guides", label: "Homeowner Guides" },
            { to: "/pros", label: "For Pest Control Pros" },
          ])}
          {col("Legal", [
            { to: "/privacy", label: "Privacy Policy" },
            { to: "/terms", label: "Terms of Use" },
            { to: "/affiliate-disclosure", label: "Affiliate Disclosure" },
          ])}
        </div>

        <div className="mt-12 pt-8 border-t border-cream/10">
          <p className="text-xs leading-relaxed text-cream/50 max-w-4xl">{LEGAL_FOOTER}</p>
          <p className="mt-6 text-xs text-cream/40">
            © {new Date().getFullYear()} DuckTheBugs. An independent consumer resource. Built for homeowners.
          </p>
        </div>
      </div>
    </footer>
  );
}
